import { initGA, gaEcommerce, gaItemsFromCart } from "@/lib/ga";

/**
 * "Pending cart" — items que o WordPress quer pôr no carrinho mas o
 * utilizador ainda não está autenticado. São guardados em sessionStorage
 * (24h, vai-se embora ao fechar o browser) e "claimados" automaticamente
 * pelo `LoginPage` / `RegisterPage` assim que houver sessão MY válida.
 *
 * Formato `items`: array de `{ id_produto, quantidade }`.
 *
 * Adicionalmente guardamos:
 *  - `ihtp_cart_back_url` : URL para "Continuar a comprar" (fonte WordPress)
 *  - `ihtp_cart_post_target` : `/my/carrinho` ou `/my/checkout` (alvo após login)
 */
const KEY = "ihtp_pending_cart_items";
const BACK_KEY = "ihtp_cart_back_url";
const TARGET_KEY = "ihtp_cart_post_target";



/** Parse `?items=1313:1,4388:2` ou `?item=1313:1` → [{id_produto, quantidade}, ...]
 *  Aceita sintaxe simples `?item=1313` (qty default = 1).
 */
export function parseItemsParam(raw) {
  if (!raw) return [];
  return String(raw)
    .split(",")
    .map((pair) => {
      const [idStr, qtyStr] = pair.split(":");
      const id = parseInt(idStr, 10);
      if (!Number.isInteger(id) || id <= 0) return null;
      const qty = parseInt(qtyStr, 10);
      return { id_produto: id, quantidade: Math.max(1, Number.isInteger(qty) ? qty : 1) };
    })
    .filter(Boolean);
}

export function setPendingCart(items) {
  try {
    if (!items?.length) {
      window.sessionStorage.removeItem(KEY);
      return;
    }
    window.sessionStorage.setItem(KEY, JSON.stringify({ items, ts: Date.now() }));
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("setPendingCart:", e);
  }
}

export function getPendingCart() {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj?.items?.length) return null;
    return obj;
  } catch {
    return null;
  }
}

export function clearPendingCart() {
  try { window.sessionStorage.removeItem(KEY); }
  catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("clearPendingCart:", e);
  }
}

/** Back URL — para o botão "Continuar a comprar" no carrinho. */
export function setBackUrl(url) {
  try {
    if (!url) window.sessionStorage.removeItem(BACK_KEY);
    else window.sessionStorage.setItem(BACK_KEY, url);
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("setBackUrl:", e);
  }
}

export function getBackUrl() {
  try { return window.sessionStorage.getItem(BACK_KEY) || ""; }
  catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("getBackUrl:", e);
    return "";
  }
}

export function clearBackUrl() {
  try { window.sessionStorage.removeItem(BACK_KEY); }
  catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("clearBackUrl:", e);
  }
}

/** Alvo pós-login: `/my/carrinho` (default) ou `/my/checkout`. */
export function setPostAuthTarget(target) {
  try {
    if (!target) window.sessionStorage.removeItem(TARGET_KEY);
    else window.sessionStorage.setItem(TARGET_KEY, target);
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("setPostAuthTarget:", e);
  }
}

export function getPostAuthTarget() {
  try { return window.sessionStorage.getItem(TARGET_KEY) || ""; }
  catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("getPostAuthTarget:", e);
    return "";
  }
}

export function clearPostAuthTarget() {
  try { window.sessionStorage.removeItem(TARGET_KEY); }
  catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("clearPostAuthTarget:", e);
  }
}

/**
 * Resolve o "Continuar a comprar" URL com prioridade:
 *  1. `?back=` explícito da URL
 *  2. document.referrer (se externo a este host)
 *  3. fallback fixo
 */
export function resolveBackUrl(searchParams, fallback = "https://shop.ihavethepower.net") {
  const fromUrl = searchParams?.get?.("back") || searchParams?.get?.("BACK");
  if (fromUrl && /^https?:\/\//i.test(fromUrl)) return fromUrl;
  try {
    const ref = document.referrer || "";
    if (ref) {
      const refHost = new URL(ref).host;
      const myHost = window.location.host;
      if (refHost && refHost !== myHost) return ref;
    }
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("resolveBackUrl:", e);
  }
  return fallback;
}

/**
 * Claim os items para o carrinho do utilizador autenticado. Chama o endpoint
 * `/api/my/shop/cart/add` em loop sequencial (mais seguro do que paralelo
 * — backend legacy gere lock por sessão de cliente).
 *
 * @param {object} myApi instância do `myApi` (frontend/src/my/api.js).
 * @returns {Promise<{ added: number, failed: number }>}
 */
export async function claimPendingCart(myApi) {
  const pending = getPendingCart();
  if (!pending) return { added: 0, failed: 0 };
  // Limpa imediatamente para evitar double-claim (LoginPage chama em 2
  // sítios: handleSubmit + useEffect que reage à mudança de `user`).
  clearPendingCart();
  let added = 0; let failed = 0;
  const addedItems = [];
  for (const item of pending.items) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await myApi.cartAdd(item.id_produto, item.quantidade, "soma");
      added += 1;
      // Só entra no evento add_to_cart o que foi efetivamente adicionado.
      if (!res || res.success !== false) {
        addedItems.push({ id_produto: item.id_produto, quantidade: item.quantidade });
      }
    } catch (e) {
      failed += 1;
      if (process.env.NODE_ENV !== "production") console.warn("claimPendingCart item failed:", item, e);
    }
  }
  // add_to_cart único do "Reserva Já" — cobre os dois caminhos (directo com
  // sessão + depois do login), porque ambos passam por aqui. Lê o carrinho
  // actualizado para obter nome e preço (ajustado ao perfil). Nunca bloqueia
  // nem altera o fluxo; falhas de analytics são silenciosas.
  if (addedItems.length) {
    try {
      initGA();
      const cartResp = await myApi.cartGet();
      const items = gaItemsFromCart(cartResp, addedItems);
      const value = items.reduce((s, it) => s + it.price * it.quantity, 0);
      gaEcommerce("add_to_cart", items, value);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("claimPendingCart add_to_cart ga failed:", e);
    }
  }
  return { added, failed };
}
